import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Tabs, Tab, FooterButton } from 'Core/components';
import CustomPropTypes from 'Core/prop-types';
import { AppModal } from '../../components/app-modals';
import { deleteByWorkTicketId, updateProcess } from '../store/slice';
import { selectChannels } from '../store/selectors';
import { showModal } from 'Core/redux/main/slice';
import { selectors as locationSelectors } from 'Core/redux/location';
import { MODAL_TYPES, ConfirmModal } from 'Feature/components/app-modals';
import { set } from 'date-fns';
// Import status options from constants to ensure consistency
import { STATUS_COLORS } from '../constants/statusColors';

const { CONFIRM_MODAL } = MODAL_TYPES;

const statusOptions = Object.keys(STATUS_COLORS).filter(key => !['All', 'Default'].includes(key));

const partFieldNames = [
  'partNumber',
  'boxQuantity',
  'palletQuantity',
  'plannedQuantity',
  'locationId',
  'licensePlateNumber',
  'po',
];

const priorityOptions = Array.from({ length: 10 }, (_, i) => i + 1);

const toInputDate = value => {
  if (!value) return '';
  if (typeof value === 'string') {
    const datePart = value.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
    const mdy = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) {
      const month = mdy[1].padStart(2, '0');
      const day = mdy[2].padStart(2, '0');
      const year = mdy[3];
      return `${year}-${month}-${day}`;
    }
  }
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

const ProgramAddEdit = ({
  showModal: isShowModal,
  modalData: initialProcessData,
  onCloseModal,
}) => {
  const [processData, setProcessData] = useState(initialProcessData);
  const [editingPart, setEditingPart] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // details | log
  const [weekendOnOff, setWeekendOnOff] = useState(processData?.weekendOnOff !== undefined ? processData.weekendOnOff : true);
  const [continuousRun, setContinuousRun] = useState(processData?.continuousRun || false);

  const locations = useSelector(locationSelectors.selectLocations);
  const channels = useSelector(selectChannels);
  const dispatch = useDispatch();

  useEffect(() => {
    setProcessData(initialProcessData);
    setActiveTab('details'); // reset tab when modal opens with new data
    setWeekendOnOff(initialProcessData?.weekendOnOff !== undefined ? initialProcessData.weekendOnOff : true);
    setContinuousRun(initialProcessData?.continuousRun || false);
  }, [initialProcessData]);

  // Ensure modal fields get populated from API fields (since/till)
  // (reverted) keep original initialization only from initialProcessData

  const handleClose = () => {
    onCloseModal();
  };

  const handleToggleChange = (field, value) => {
    setIsFormDirty(true);
    if (field === 'weekendOnOff') {
      setWeekendOnOff(value);
      setProcessData(prevData => ({ ...prevData, weekendOnOff: value }));
    } else if (field === 'continuousRun') {
      setContinuousRun(value);
      setProcessData(prevData => ({ ...prevData, continuousRun: value }));
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setIsFormDirty(true);
    if (partFieldNames.some(fieldName => name.startsWith(fieldName))) {
      const index = parseInt(name.split('-')[1], 10);
      const fieldName = name.split('-')[0];
      const updatedParts = processData.processParts.map((part, i) =>
        i === index ? { ...part, [fieldName]: value } : part,
      );
      setProcessData(prevData => ({ ...prevData, processParts: updatedParts }));
    } else {
      // When machineId changes, also update channelUuid for consistency
      if (name === 'machineId') {
        setProcessData(prevData => ({
          ...prevData,
          machineId: value,
          channelUuid: value, // Sync channelUuid with machineId
        }));
      } else if (name === 'fromDate' || name === 'startTime') {
        // Handle date and time combination for fromDate/startTime (original behavior)
        setProcessData(prevData => {
          const currentFromDate = prevData.fromDate || new Date().toISOString();
          const currentDate = new Date(currentFromDate);

          let newDate;
          if (name === 'fromDate') {
            // Date changed - combine with existing time
            const timePart = currentDate.toTimeString().slice(0, 5);
            newDate = new Date(`${value}T${timePart}:00`);
          } else {
            // Time changed - combine with existing date
            const datePart = currentDate.toISOString().split('T')[0];
            newDate = new Date(`${datePart}T${value}:00`);
          }

          return { ...prevData, fromDate: newDate.toISOString() };
        });
      } else if (name === 'endDate' || name === 'endTime') {
        // End Date / End Time update schedule toDate only (Due Date not auto-mapped)
        setProcessData(prevData => {
          const currentToDate = prevData.toDate || new Date().toISOString();
          const currentDate = new Date(currentToDate);

          let newDate;
          if (name === 'endDate') {
            // Date changed - combine with existing time
            const timePart = currentDate.toTimeString().slice(0, 5);
            newDate = new Date(`${value}T${timePart}:00`);
          } else {
            // Time changed - combine with existing date
            const datePart = currentDate.toISOString().split('T')[0];
            newDate = new Date(`${datePart}T${value}:00`);
          }

          return { ...prevData, toDate: newDate.toISOString() };
        });
      } else {
        setProcessData(prevData => ({ ...prevData, [name]: value }));
      }
    }
  };

  const body = () => {
    return (
      <>
        <div className="d-flex gap-2 mb-3">
          <button
            type="button"
            className={`btn ${activeTab === 'details' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('details')}
          >
            Work Ticket Details
          </button>
          <button
            type="button"
            className={`btn ${activeTab === 'log' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('log')}
          >
            Log
          </button>
        </div>

        <div className="" style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
          <div>
            <div>
              {/* Toggle Buttons */}
              <div className="row mb-3 justify-content-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-center">
                    <label className="fw-bold me-3 mb-0" style={{ color: '#0d6efd' }}>Weekend ON/OFF</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="weekendOnOff"
                        checked={weekendOnOff}
                        onChange={(e) => handleToggleChange('weekendOnOff', e.target.checked)}
                        style={{ width: '50px', height: '25px', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-center">
                    <label className="fw-bold me-3 mb-0" style={{ color: '#0d6efd' }}>Continuous Run</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="continuousRun"
                        checked={continuousRun}
                        onChange={(e) => handleToggleChange('continuousRun', e.target.checked)}
                        style={{ width: '50px', height: '25px', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Priority</label>
                    <select
                      className="form-control"
                      name="priority"
                      value={processData.priority || ''}
                      onChange={handleChange}
                    >
                      {priorityOptions.map((priority, index) => (
                        <option key={index} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* <div className="mb-1">
                   <label className="fw-bold">Status</label>
                   <div className="d-flex align-items-center">
                     <div 
                       className="me-2 rounded" 
                       style={{ 
                         width: '20px', 
                         height: '20px', 
                         backgroundColor: STATUS_COLORS[processData.status] || STATUS_COLORS.Default,
                         border: '1px solid #ccc'
                       }}
                       title={`Status color: ${STATUS_COLORS[processData.status] || STATUS_COLORS.Default}`}
                     ></div>
                     <select
                       className="form-control"
                       name="status"
                       value={processData.status || ''}
                       onChange={handleChange}
                     >
                       {statusOptions.map((status, index) => (
                         <option key={index} value={status}>
                           {status}
                         </option>
                       ))}
                     </select>
                   </div>
               </div> */}
                  <div className="mb-1">
                    <label className="fw-bold">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="endDate"
                      value={toInputDate(
                        processData.toDate ||
                          processData.ToDate ||
                          processData.endDate ||
                          processData.EndDate ||
                          processData.till,
                      )}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="fromDate"
                      value={
                        processData.fromDate
                          ? new Date(processData.fromDate).toISOString().split('T')[0]
                          : ''
                      }
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mb-1">
                    <label className="fw-bold">Start Time</label>
                    <input
                      type="time"
                      className="form-control"
                      name="startTime"
                      value={
                        processData.fromDate
                          ? new Date(processData.fromDate).toTimeString().slice(0, 5)
                          : ''
                      }
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Due Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="dueDate"
                      value={toInputDate(processData.dueDate || processData.DueDate || '')}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mb-1">
                    <label className="fw-bold">End Time</label>
                    <input
                      type="time"
                      className="form-control"
                      name="endTime"
                      value={
                        processData.toDate
                          ? new Date(processData.toDate).toTimeString().slice(0, 5)
                          : ''
                      }
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Machine</label>
                    <select
                      className="form-control"
                      name="machineId"
                      value={processData.machineId || ''}
                      onChange={handleChange}
                    >
                      {channels.map(channel => (
                        <option key={channel.uuid} value={channel.uuid}>
                          {channel.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Mold Cavity Count</label>
                    <input
                      type="number"
                      className="form-control"
                      name="moldCavityCount"
                      value={processData.moldCavityCount || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Cycle Time</label>
                    <input
                      type="text"
                      className="form-control"
                      name="cycleTime"
                      value={processData.cycleTime || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Work Ticket Number</label>
                    <input
                      type="text"
                      className="form-control"
                      name="workTicketNumber"
                      value={processData.workTicketNumber || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Origin Address</label>
                    <input
                      type="text"
                      className="form-control"
                      name="originAddress"
                      value={processData.originAddress || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">OPCode</label>
                    <input
                      type="text"
                      className="form-control"
                      name="opCode"
                      value={processData.opCode || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Status</label>
                    <div className="d-flex align-items-center">
                      <div
                        className="me-2 rounded"
                        style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor:
                            STATUS_COLORS[processData.status] || STATUS_COLORS.Default,
                          border: '1px solid #ccc',
                        }}
                        title={`Status color: ${STATUS_COLORS[processData.status] || STATUS_COLORS.Default}`}
                      ></div>
                      <select
                        className="form-control"
                        name="status"
                        value={processData.status || ''}
                        onChange={handleChange}
                      >
                        {statusOptions.map((status, index) => (
                          <option key={index} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
                {processData?.processParts?.map((part, index) => (
                  <fieldset key={index} className="border p-2 m-3">
                    <legend className="w-auto">Part {index + 1}</legend>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Part Number</label>
                          <input
                            type="text"
                            className="form-control"
                            name={`partNumber-${index}`}
                            value={part.partNumber || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Box Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`boxQuantity-${index}`}
                            value={part.boxQuantity || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Boxes per Pallet</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`palletQuantity-${index}`}
                            value={part.palletQuantity || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Planned Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`plannedQuantity-${index}`}
                            value={part.plannedQuantity || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Location</label>
                          <select
                            className="form-control"
                            name={`locationId-${index}`}
                            value={part.locationId || ''}
                            onChange={handleChange}
                          >
                            <option key="0" value="0">
                              Select a Location
                            </option>
                            {locations.map(location => (
                              <option key={location.id} value={location.id}>
                                {location.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">License Plate Number</label>
                          <input
                            type="text"
                            className="form-control"
                            name={`licensePlateNumber-${index}`}
                            value={part.licensePlateNumber || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">PO</label>
                          <input
                            type="text"
                            className="form-control"
                            name={`po-${index}`}
                            value={part.po || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  </fieldset>
                ))}
              </div>
            </div>
          </div>
        </div>

        {activeTab === 'log' && (
          <div className="p-3">
            <div className="alert alert-secondary mb-2">Log</div>
            <p className="text-muted mb-0">No log entries available.</p>
          </div>
        )}
      </>
    );
  };

  const valid = () => {
    if (false) {
      //   dispatch(
      //     showToast({
      //       message: 'Please fill all required fields',
      //       type: 'danger',
      //     }),
      //   );
      return false;
    }
    return true;
  };

  const footer = () => {
    const onSave = () => {
      if (valid()) {
        const payload = { ...processData };
        delete payload.materialBatchNumber;
        delete payload.MaterialBatchNumber;
        dispatch(updateProcess(payload));
      }
    };

    const onConfirmDelete = () => {
      // Be robust: some payloads may not include id directly
      const deleteId =
        processData?.id ||
        processData?.workTicketNumber ||
        processData?.workTicketNo ||
        processData?.workTicket ||
        processData?.processIdExt;

      if (!deleteId) {
        console.warn('Delete requested but no valid ID found in processData', processData);
        return;
      }

      dispatch(deleteByWorkTicketId(deleteId));
      setShowDeleteModal(false);
      handleClose();
    };

    return (
      <div className="row">
        <div className="col-lg-8">
          <button className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
            Delete Work Item
          </button>

          <ConfirmModal
            showModal={showDeleteModal}
            title="Delete Work Item"
            message="Are you sure you want to delete this work item?"
            onCloseModal={() => setShowDeleteModal(false)}
            onConfirmModal={onConfirmDelete}
          />
        </div>
        <div className="col-lg-4">
          <FooterButton
            cancelText="Cancel"
            saveText="Save Plan"
            onSave={onSave}
            onCancel={handleClose}
            disableSave={!isFormDirty}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="program-modal"
      show={isShowModal}
      size="xl"
      title="Plan"
      body={body}
      footer={footer}
      handleClose={handleClose}
    />
  );
};

ProgramAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.shape(CustomPropTypes.Shipment),
  onCloseModal: PropTypes.func,
};

export default AppModal(ProgramAddEdit);
